﻿import { useCallback, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { useT } from "@/lib/i18n";
import type { UploadKind } from "@ticketnjoy/types";
import { ApiError, uploadImage } from "@/lib/api-client";
import { cn } from "@/lib/utils";

interface Props {
  /** Mevcut görselin URL'i. Form state'ten geliyor; null/undefined → boş. */
  value: string | null | undefined;
  /** Upload başarılı olduğunda yeni URL ile çağrılır. null gelirse kaldırma. */
  onChange: (url: string | null) => void;
  kind: UploadKind;
  eventId?: string;
  shopId?: string;
  /**
   * En-boy oranı — "cover" (16/9 banner), "logo" (1/1 square) vb.
   * Sadece preview için; backend bir dönüşüm yapmaz.
   */
  aspect?: "cover" | "square";
  /**
   * Değiştir/Kaldır butonlarının yerleşimi:
   *  - "below" (varsayılan): preview altında — geniş kapak/banner için uygun.
   *  - "beside": 64×64 sabit preview'in yanında — logo/avatar gibi kompakt kullanımlar için.
   *    "beside" modunda `aspect` prop'u ignore edilir, preview 64×64 sabittir.
   */
  actionsPosition?: "below" | "beside";
  className?: string;
  disabled?: boolean;
}

const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Reusable image uploader — drag-drop + click-to-select + preview + progress.
 *
 * Flow:
 *   1. User drops/selects file → local validation (size, MIME).
 *   2. uploadImage() → backend /organizer/uploads → S3.
 *   3. Başarı → onChange(url). Parent form state'ine yazar, sonraki Save ile DB'ye gider.
 *   4. Kaldırma → onChange(null). (S3'teki dosyayı delete etmiyoruz; eski URL sadece formdan çıkar.
 *      Submit sonrası DB'de kolon null olur. Eski dosyayı temizleme v2 işi.)
 */
export function ImageUploader({
  value,
  onChange,
  kind,
  eventId,
  shopId,
  aspect = "cover",
  actionsPosition = "below",
  className,
  disabled = false,
}: Props) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      if (!ALLOWED_MIMES.includes(file.type)) {
        setError(t("upload.errorType"));
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        setError(t("upload.errorSize"));
        return;
      }

      setProgress(0);
      try {
        const res = await uploadImage({
          file,
          kind,
          eventId,
          shopId,
          onProgress: setProgress,
        });
        onChange(res.url);
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.detail : t("upload.errorGeneric");
        setError(msg);
      } finally {
        setProgress(null);
      }
    },
    [kind, eventId, shopId, onChange, t],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      const file = e.dataTransfer.files?.[0];
      if (file) void handleFile(file);
    },
    [handleFile, disabled],
  );

  const isBeside = actionsPosition === "beside";
  // "beside" modunda preview her zaman 64×64 sabit — logo/avatar için uygun.
  const previewSizeClass = isBeside
    ? "h-16 w-16 shrink-0"
    : aspect === "square"
      ? "aspect-square"
      : "aspect-[16/9]";

  const previewNode = (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border-2 border-dashed transition",
        previewSizeClass,
        isDragging
          ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5"
          : "border-border bg-muted/30",
        disabled && "opacity-60",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
    >
      {value ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={value}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || progress !== null}
          className={cn(
            "flex h-full w-full flex-col items-center justify-center gap-2 text-center text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed",
            // 64×64 alanda sadece ikon + küçük label, uzun format açıklaması yer almaz.
            isBeside ? "p-1" : "p-4",
          )}
        >
          <ImageIcon className={cn(isBeside ? "h-5 w-5" : "h-8 w-8")} />
          {!isBeside && (
            <>
              <p className="text-sm font-medium">{t("upload.dropHint")}</p>
              <p className="text-xs">{t("upload.formats")}</p>
            </>
          )}
        </button>
      )}

      {progress !== null ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 backdrop-blur-sm">
          <Loader2
            className={cn(
              "animate-spin text-[color:var(--brand)]",
              isBeside ? "h-4 w-4" : "h-6 w-6",
            )}
          />
          {!isBeside && (
            <p className="text-sm font-medium">
              {t("upload.uploading", { pct: progress })}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );

  const actionsNode =
    value && progress === null ? (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" />
          {t("upload.change")}
        </button>
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {t("upload.remove")}
        </button>
      </div>
    ) : null;

  return (
    <div className={cn("space-y-2", className)}>
      {isBeside ? (
        <div className="flex items-center gap-3">
          {previewNode}
          {actionsNode}
        </div>
      ) : (
        <>
          {previewNode}
          {actionsNode}
        </>
      )}

      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIMES.join(",")}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
